import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { ChannelLogo } from './channel-logo';
import { BookmarkIcon, ChevronIcon, GlobeIcon } from './icons';
import { useToggleSaveMutation } from '../hooks/use-feed-query';
import { useProfileQuery } from '../hooks/use-profile-query';
import { getLanguageName } from '../config/reference-data';
import type { FeedArticle } from '../types';

interface ArticleCardProps {
  readonly article: FeedArticle;
  readonly isSaved?: boolean;
}

function formatTimeAgo(iso: string | null, t: TFunction): string {
  if (!iso) return '';
  const date = new Date(iso);
  const ms = Date.now() - date.getTime();
  if (Number.isNaN(ms)) return '';
  // Articles can land with a publishedAt slightly in the future of the
  // client clock (RSS feeds occasionally report future timestamps, and
  // upstream channel servers drift from our query time). Render those
  // as "just now" rather than dropping the timestamp entirely — a blank
  // time field reads as a bug to users.
  if (ms < 0) return t('web.articleCard.timeAgo.justNow');
  const min = Math.round(ms / 60_000);
  if (min < 1) return t('web.articleCard.timeAgo.justNow');
  if (min < 60) return t('web.articleCard.timeAgo.minutesAgo', { count: min });
  const hr = Math.round(min / 60);
  if (hr < 24) return t('web.articleCard.timeAgo.hoursAgo', { count: hr });
  const day = Math.round(hr / 24);
  if (day < 7) return t('web.articleCard.timeAgo.daysAgo', { count: day });
  const wk = Math.round(day / 7);
  return t('web.articleCard.timeAgo.weeksAgo', { count: wk });
}

// Reveal fraction of the TEXT region only — the image stays put. The
// front slides 100% out so the original-language copy is fully visible
// (any value < 1 leaves the rightmost portion of the original hidden
// behind the front). To close: tap the chevron toggle (always reachable
// at z:3 above the layers) or drag back from anywhere on the back.
const REVEAL_FRACTION = 1.0;
const DRAG_THRESHOLD_PX = 6;
const SNAP_THRESHOLD_PX = 60;

export function ArticleCard({ article, isSaved = false }: ArticleCardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toggleSave = useToggleSaveMutation();
  const profile = useProfileQuery();
  const time = formatTimeAgo(article.publishedAt, t);
  const fallbackEnglish = t('common.fallbacks.english');

  // Peek-to-original gesture is only enabled when the backend ships the
  // original-language copy alongside the translated payload. Mirrors
  // mobile's `hasOriginalContent` gate in FullScreenArticleCard.
  const hasOriginal =
    article.isTranslated && !!article.originalTitle && !!article.originalLanguage;

  const sourceLangCode = article.originalLanguage ?? article.translatedFrom ?? '';
  const targetLangCode = profile.data?.primaryLanguage ?? article.language;
  const sourceLangName = getLanguageName(sourceLangCode);
  const targetLangName = getLanguageName(targetLangCode);

  // ── Peek state ───────────────────────────────────────────────────────
  // `revealed` drives the rest position (CSS class). During an active
  // drag we bypass React entirely and mutate the front layer's transform
  // directly via ref — re-rendering on every pointermove would ship the
  // whole card subtree through reconciliation 60 times a second.
  const [revealed, setRevealed] = useState(false);
  const articleRef = useRef<HTMLElement | null>(null);
  const peekRef = useRef<HTMLDivElement | null>(null);
  const frontRef = useRef<HTMLDivElement | null>(null);
  const startXRef = useRef<number | null>(null);
  const baselineRef = useRef(0);
  const widthRef = useRef(0);
  const draggedRef = useRef(false);

  // Pointer capture is acquired LAZILY — only once the user crosses the
  // drag threshold. Capturing on pointerdown would retarget the eventual
  // mouseup to <article>, and the click event's target becomes the
  // common ancestor of pointerdown + pointerup — i.e. <article> itself,
  // not the inner <h3 onClick={open}>. The title would then be unclickable
  // on every peekable (translated) card. By delaying capture to the
  // post-threshold path we keep clean clicks routing to the h3 while still
  // tracking the gesture across the article boundary during a real drag.
  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (!hasOriginal) return;
      // Only respond to primary-button mouse / touch / pen — ignore
      // right-click, middle-click etc. which bubble through the same
      // pointer events.
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      const node = articleRef.current;
      const peek = peekRef.current;
      if (!node || !peek) return;
      // Width is measured against the text region (peek wrapper), not
      // the whole article — only text moves, only text-region width
      // matters for the snap calculation.
      widthRef.current = peek.getBoundingClientRect().width;
      startXRef.current = e.clientX;
      baselineRef.current = revealed ? widthRef.current * REVEAL_FRACTION : 0;
      draggedRef.current = false;
      // NOTE: no setPointerCapture here — see comment block above.
    },
    [hasOriginal, revealed],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (startXRef.current === null) return;
      const delta = e.clientX - startXRef.current;
      if (Math.abs(delta) > DRAG_THRESHOLD_PX) {
        if (!draggedRef.current) {
          draggedRef.current = true;
          // First time we've decided this is a drag — capture the pointer
          // so subsequent move/up reach us even if the user drags off the
          // article. The click event for this gesture is already destined
          // to be swallowed by onClickCapture, so retargeting mouseup
          // here doesn't break the title-click path.
          try {
            e.currentTarget.setPointerCapture(e.pointerId);
          } catch {
            // Capture can fail if the pointer was released between
            // events — non-fatal, the gesture just ends.
          }
          articleRef.current?.classList.add('is-dragging');
        }
      }
      // Don't animate until we're sure it's a drag — sub-threshold
      // movement during a click should leave the front layer untouched.
      if (!draggedRef.current) return;
      const max = widthRef.current * REVEAL_FRACTION;
      const next = Math.max(0, Math.min(max, baselineRef.current + delta));
      if (frontRef.current) {
        frontRef.current.style.transform = `translateX(${next}px)`;
      }
    },
    [],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (startXRef.current === null) return;
      startXRef.current = null;
      articleRef.current?.classList.remove('is-dragging');
      try {
        // Safe to call even when capture was never set — releasePointerCapture
        // is a no-op for pointers that aren't being captured.
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // Same caveat as setPointerCapture — release can throw if the
        // capture was already lost. Safe to swallow.
      }
      const matrix = frontRef.current?.style.transform ?? '';
      const match = /translateX\(([-0-9.]+)px\)/.exec(matrix);
      const current = match ? parseFloat(match[1] ?? '0') : 0;
      const max = widthRef.current * REVEAL_FRACTION;
      const target = revealed
        ? current > max / 2 // dragging back from revealed — snap closed if past halfway
        : current > SNAP_THRESHOLD_PX; // opening — snap open if past threshold
      if (frontRef.current) frontRef.current.style.transform = '';
      setRevealed(target);
    },
    [revealed],
  );

  const open = () => navigate(`/article/${encodeURIComponent(article.id)}`);

  // Props shared between the front (translated) and back (original)
  // title elements — both behave as buttons that open the reader. The
  // article wrapper no longer carries onClick, so the title is the
  // only path to /article/:id; channel cluster and tag chips have
  // their own dedicated handlers (channel feed, tag feed).
  const titleButtonProps = {
    role: 'button' as const,
    tabIndex: 0,
    onClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      open();
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.stopPropagation();
      e.preventDefault();
      open();
    },
  };

  // Capture-phase click guard: if the gesture turned into a drag, swallow
  // the synthetic click before it reaches descendants (bookmark button,
  // tag chips). Without this, dragging past the bookmark would toggle
  // save on release.
  const onClickCapture = (e: React.MouseEvent<HTMLElement>) => {
    if (draggedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      draggedRef.current = false;
    }
  };

  // Click-toggle equivalent of the drag gesture — instant flip between
  // closed and revealed states, animated by the same CSS transition the
  // drag-snap uses. stopPropagation prevents the click from bubbling to
  // the article (which would navigate to the reader).
  const togglePeek = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (!hasOriginal) return;
      setRevealed((v) => !v);
    },
    [hasOriginal],
  );

  // Don't let a pointerdown on the toggle button start a drag — the
  // article-level pointerdown handler would otherwise capture the
  // pointer and the click event we want would never fire.
  const stopPointerDown = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    e.stopPropagation();
  }, []);

  const save = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleSave.mutate({
      articleHash: article.id,
      save: !isSaved,
      // Backend SaveArticleDto requires title + sourceUrl; pass through the
      // rest so the saved record renders standalone in /saved.
      article: !isSaved
        ? {
            title: article.title,
            summary: article.summary,
            imageUrl: article.imageUrl ?? null,
            sourceUrl: article.sourceUrl,
            channelName: article.channelName,
            channelLogo: article.channelLogo ?? null,
            language: article.language,
            publishedAt: article.publishedAt,
            isTranslated: article.isTranslated,
            translatedFrom: article.translatedFrom ?? null,
          }
        : undefined,
    });
  };

  return (
    // Article wrapper is no longer a click target — click on the TITLE
    // to open the reader. Channel cluster and tag chips have their own
    // click handlers (channel feed, tag feed). The wrapper still owns
    // the peek-drag pointer handlers and the onClickCapture suppression
    // (so a drag doesn't fire a click on whichever descendant the
    // pointer ended on).
    <article
      ref={articleRef}
      className={`nw-article ${revealed ? 'is-revealed' : ''} ${hasOriginal ? 'is-peekable' : ''}`}
      onClickCapture={onClickCapture}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {/* Image region — never moves during peek. Lives outside the
          peek wrapper so the drag gesture only translates the text. */}
      <div className="nw-article__media">
        {article.imageUrl && <img src={article.imageUrl} alt="" loading="lazy" />}
        {article.isTranslated && sourceLangName && (
          // Mobile renders this same pill in the top-left of the image:
          // globe icon + "{source} → {target}".
          <span className="nw-article__lang-pill">
            <GlobeIcon size={11} stroke={2} />
            {t('web.articleCard.translatedBadge', {
              source: sourceLangName,
              target: targetLangName || fallbackEnglish,
            })}
          </span>
        )}
      </div>

      {/* Peek region — text only. Layers the original-language copy
          (back) underneath the translated copy (front). Both layers use
          the same .nw-article__body class so corresponding rows
          (source / badge → title → bullets → meta / hint) align
          horizontally — the user's original-language title appears at
          exactly the same Y as the translated title behind it. */}
      <div className="nw-article__peek" ref={peekRef}>
        {hasOriginal && (
          // Always-reachable toggle. Sits at z:3 (above front + back) so
          // it stays clickable both when closed (chevron points right —
          // "open the peek") and when revealed (chevron points left —
          // "close it"). Drag still works from anywhere on the card; the
          // button is a discoverable click-equivalent for users who
          // don't realise the card is draggable.
          <button
            type="button"
            className={`nw-article__peek-toggle ${revealed ? 'is-on' : ''}`}
            aria-label={revealed ? t('web.articleCard.a11y.hideOriginal') : t('web.articleCard.a11y.showOriginal')}
            aria-pressed={revealed}
            onClick={togglePeek}
            onPointerDown={stopPointerDown}
          >
            <ChevronIcon dir={revealed ? 'left' : 'right'} size={14} stroke={2.4} />
          </button>
        )}
        {hasOriginal && (
          <div className="nw-article__back" aria-hidden={!revealed}>
            <div className="nw-article__body">
              <div className="nw-article__source-row">
                <span className="nw-article__back-badge">
                  <GlobeIcon size={12} stroke={2} />
                  {t('web.articleCard.originalBadge', { language: sourceLangName })}
                </span>
              </div>
              <h3
                className="nw-article__title"
                aria-label={t('web.articleCard.a11y.openArticle', { title: article.originalTitle })}
                {...titleButtonProps}
              >
                {article.originalTitle}
              </h3>
              {article.originalSummaryBullets?.length ? (
                <ul className="nw-article__bullets">
                  {article.originalSummaryBullets.slice(0, 3).map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              ) : null}
              <div className="nw-article__meta">
                <span className="nw-article__back-hint">{t('web.articleCard.dragToDismiss')}</span>
              </div>
            </div>
          </div>
        )}

        <div className="nw-article__front" ref={frontRef}>
          <div className="nw-article__body">
            <div className="nw-article__source-row">
              {/* Channel logo + name share a single click target — same
                  pattern as mobile's FullScreenArticleCard, where the
                  whole channel cluster is wrapped in a TouchableOpacity
                  that opens the ChannelFeed. stopPropagation prevents
                  the click from bubbling to the article (which would
                  navigate to the reader instead). */}
              <span
                className="nw-article__channel-link"
                role="link"
                tabIndex={0}
                aria-label={t('web.articleCard.a11y.openChannel', { channel: article.channelName })}
                onClick={(e) => {
                  e.stopPropagation();
                  if (article.channelId) {
                    navigate(`/channel/${encodeURIComponent(article.channelId)}`);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  e.stopPropagation();
                  e.preventDefault();
                  if (article.channelId) {
                    navigate(`/channel/${encodeURIComponent(article.channelId)}`);
                  }
                }}
              >
                <ChannelLogo name={article.channelName} logoUrl={article.channelLogo} size={26} />
                <span className="nw-article__source">{article.channelName}</span>
              </span>
              {time && <span className="nw-article__time">· {time}</span>}
            </div>
            <h3
              className="nw-article__title"
              aria-label={t('web.articleCard.a11y.openArticle', { title: article.title })}
              {...titleButtonProps}
            >
              {article.title}
            </h3>
            {article.summaryBullets?.length > 0 && (
              <>
                <span className="nw-article__ai-badge">{t('common.aiSummaryBadge')}</span>
                <ul className="nw-article__bullets">
                  {article.summaryBullets.slice(0, 3).map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              </>
            )}
            <div className="nw-article__meta">
              <div className="nw-article__tags">
                {article.tags?.slice(0, 3).map((t) => (
                  <span
                    key={t}
                    className="nw-tag"
                    role="link"
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/tag/${encodeURIComponent(t)}`);
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter') return;
                      e.stopPropagation();
                      e.preventDefault();
                      navigate(`/tag/${encodeURIComponent(t)}`);
                    }}
                  >
                    #{t}
                  </span>
                ))}
              </div>
              <button
                type="button"
                className={`nw-bookmark-btn ${isSaved ? 'is-on' : ''}`}
                onClick={save}
                aria-label={isSaved ? t('web.articleCard.a11y.removeFromSaved') : t('web.articleCard.a11y.saveArticle')}
              >
                <BookmarkIcon size={14} stroke={2.2} filled={isSaved} />
              </button>
            </div>
          </div>
        </div>
      </div>

    </article>
  );
}
